
/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: Explore (C) Copyright IBM Corp. 2020, 2021
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */

import React, { Component } from 'react';
import PropTypes from 'prop-types';
import StringResources from './nls/StringResources';
import '../stylesheets/overlay.scss';
import DndSlotItemHandler from './DndSlotItemHandler';
import { Tags, TruncatedText } from '@ba/ba-ui-carbon-toolkit';

const MAX_TAGS_DISPLAYED = 2;
const COLUMN_LABEL_MAX_WIDTH = 94; // adjusted from R7 setting (90px) to get similar truncated text for dropzones in 11.2 as Carbon tags has padding/margin inside bx--tags element

/**
 * The Overlay Drop Zone View
 */
class OverlayDropZone extends Component {
	componentDidMount() {
		this._initializeDndSupport();
	}

	componentWillUnmount(){
		this.dropSlotItemHandler && this.dropSlotItemHandler.destroy();
	}

	render() {
		let className = 'overlayDropZone';
		if (this.props.className) {
			className += ' ' + this.props.className;
		}
		let items = [];
		if (this.props.slot) {
			items = this.props.slot.getDataItemList();
		}
		const dropZoneCaption = this.props.slot.getDefinition().getCaption();
		const slotId = this.props.slot.getDefinition().getId();
		return (
			<div className={className}>
				<div className='overlayDropZoneCaption'>
					{(this._isRequiredField()) ?
						<span className="overlayDropZoneCaptionRequiredStar">*</span>: null }
					{dropZoneCaption}
				</div>
				<div className='overlayDropZonePlaceHolder' data-slot-id={slotId} ref={(node) => { this.domNode = node; }}>
					{this._renderItems(items)}
				</div>
			</div>
		);
	}
	_isRequiredField() {
		return !this.props.slot.getDefinition().isOptional() && this.props.slot.getDefinition().getShowRequiredMarker();
	}

	//Drag and Drop Support.
	_initializeDndSupport() {
		if (this.props.dndManager && this.domNode) {
			const dndManager = this.props.dndManager;
			//Initialize a drop handler for both the item node and the 'after node'.
			//Also initialize the node as a drag source.
			this._dndManager = dndManager;
			this.dropSlotItemHandler = new DndSlotItemHandler({
				domNode: this.domNode,
				className: 'dndHover',
				slotDataItemHandler: this.props.slotDataItemHandler,
				addAfter: false,
				isVisOverlay: true,
				onDragEnter: this._onDragEnterExecute.bind(this),
				onDragLeave: this._onDragLeaveExecute.bind(this),
				dndManager: dndManager
			});
		}
	}

	_onDragEnterExecute(){
		const dataViewNode = this._getDataViewNode();
		if(dataViewNode){
			dataViewNode.classList.add('notOnFocus');
		}
	}

	_onDragLeaveExecute(){
		const dataViewNode = this._getDataViewNode();
		if(dataViewNode){
			dataViewNode.classList.remove('notOnFocus');
		}
	}

	_getDataViewNode() {
		if(this.domNode) {
			const _closest = (el, className) => {
				// Traverse the DOM up with a while loop
				while (el.className.indexOf(className) === -1) {
					// Increment the loop to the parent node
					el = el.parentNode;
					if (!el) {
						return null;
					}
				}
				return el;
			};
			const widgetContentNode = _closest(this.domNode, 'liveWidgetContent');
			if(widgetContentNode){
				const dataViewNode = widgetContentNode.querySelector('.dataview');
				return dataViewNode;
			}
		}
	}

	_getSlotDataItemHandler() {
		return this.props.slotDataItemHandler;
	}

	_renderItems(items) {
		let content;
		const tags = [];
		if (items.length) {
			const slotHasUnavailableColumns = this.props.slot.hasUnavailableMetadataColumns();
			let tagLabel;
			items.forEach((item, index) => {
				tagLabel = null;
				if (index < MAX_TAGS_DISPLAYED) {
					tagLabel = this._getSlotItemLabel(item, slotHasUnavailableColumns);
				} else if (index === MAX_TAGS_DISPLAYED) {
					// If there are more than 3 data items mapped to the slot, display "+N" as the last tag where N is the number of remaining mapped items
					tagLabel = '+' + (items.length - MAX_TAGS_DISPLAYED);
				}
				if (tagLabel) {
					tags.push ({
						value: index++,
						label: <TruncatedText value={tagLabel}/>,
						color: 'gray'
					});
				}
			});
			content = (
				<div className='overlayDropZoneItems'>
					<Tags
						id="default"
						tags={tags}
						fullWidth={false}
						removable={false}
						style={ { 'max-width': `${COLUMN_LABEL_MAX_WIDTH}px` } }
					/>
					<div className='overlayDropZoneItemsInsertAfter'></div>
				</div>
			);
		} else {
			content = (<div className='overlayDropZonePlaceHolderText'>{StringResources.get('overlayDropZonePlaceHolderText')}</div>);
		}
		return content;
	}

	_getSlotItemLabel(dataItem, slotHasUnavailableColumns) {
		let label = dataItem.getLabel();
		if (slotHasUnavailableColumns) {
			// check if the current data item is the missing one
			const metadataColumn = dataItem.getMetadataColumn();
			if (!metadataColumn) {
				label = StringResources.get('missingColumn', { 'columnLabel': label });
			}
		}
		return label;
	}
}

OverlayDropZone.propTypes = {
	className: PropTypes.string,
	slot: PropTypes.object.isRequired,
	dndManager: PropTypes.object.isRequired,
	dashboard: PropTypes.object.isRequired,
	slotDataItemHandler: PropTypes.object.isRequired
};
OverlayDropZone.defaultProps = {
	className: ''
};

export default OverlayDropZone;



// WEBPACK FOOTER //
// ./src/js/OverlayDropZone.js